import { FC, useEffect, useMemo, useState } from "react";
import { Box, Divider, Typography } from "@material-ui/core";
import { CheckCircle as CheckCircleIcon } from "@material-ui/icons";
import { isEqual } from "lodash";
import { usePostHog } from "posthog-js/react";

import {
  Button,
  ErrorMessage,
  Modal,
  TermsAndConditions,
  WarningMessage,
} from "components";
import { useAssetDetailData } from "containers/AssetDetail/hooks";
import { usePrevious } from "hooks";
import {
  formatAmount,
  formatCurrencyAmount,
  formatDate,
  formatNumber,
} from "lib/format";
import { parseNumber } from "lib/parse";
import { DEFAULT_ERROR_MESSAGE } from "services/constants";
import { BaseAmountSource } from "services/listings/types";
import { increaseOffer } from "services/offers";
import { ListingOffer } from "services/offers/types";
import { createProxy, deleteProxy } from "services/proxies";
import { Proxy } from "services/proxies/types";
import { Track } from "services/valuation/types";
import theme from "theme";
import { circleIconSize } from "theme/constants";

import TransactionFeeNotice from "../TransactionFeeNotice";
import EditOfferForm from "./EditOfferForm";

interface EditOfferModalProps {
  onClose: () => void;
  buyNowPrice: number;
  dollarAge: number;
  highestOpenBuyerOfferAmount: number;
  listingId: number;
  listingProxyIncrementAmount: number;
  lte: number;
  ltm: number;
  marketplaceMedian: string | null;
  marketplaceMedianMultiplier: number;
  marketplaceMedianSource: BaseAmountSource;
  offer: ListingOffer;
  offers: ListingOffer[];
  openProxy: Proxy;
  termExpiration: string | undefined;
  trackList: Track[];
  valuationId: number;
}

const titleByStep = ["Edit Offer", "Confirm Offer", "Offer Edited"];

const EditOfferModal: FC<EditOfferModalProps> = ({
  onClose,
  buyNowPrice,
  dollarAge,
  highestOpenBuyerOfferAmount,
  listingId,
  listingProxyIncrementAmount,
  lte,
  ltm,
  marketplaceMedian,
  marketplaceMedianMultiplier,
  marketplaceMedianSource,
  offer,
  offers,
  openProxy,
  termExpiration,
  trackList,
  valuationId,
}) => {
  const initialEditOfferValues = {
    offerAmount: offer.amount.toString(),
    proxyAmount: openProxy?.amount,
  };

  const { handleRefreshListing, handleRefreshProxy } = useAssetDetailData(
    listingId,
  );
  const posthog = usePostHog();

  const [step, setStep] = useState(0);
  const [values, setValues] = useState(initialEditOfferValues);
  const [isLoading, setIsLoading] = useState(false);
  const [statisticsChanged, setStatisticsChanged] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const makeOnBackHandler = () => {
    if (step === 1) {
      return () => {
        setStatisticsChanged(false);
        setErrorMessage("");
        setStep(0);
      };
    } else {
      return undefined;
    }
  };

  const statistics = useMemo(
    () => ({
      lte,
      tracklistLength: trackList.length,
      buyNowPrice,
      offers,
    }),
    [lte, trackList, buyNowPrice, offers],
  );
  const previousStatistics = usePrevious(statistics);
  useEffect(() => {
    if (!!previousStatistics && !isEqual(statistics, previousStatistics)) {
      setStatisticsChanged(true);
    }
  }, [statistics, previousStatistics]);

  const handleClose = () => {
    setStatisticsChanged(false);
    setErrorMessage("");
    onClose();
  };

  const handleIncreaseYourOffer = async () => {
    try {
      setIsLoading(true);
      if (!isEqual(values.proxyAmount, initialEditOfferValues.proxyAmount)) {
        if (!!values.proxyAmount) {
          await createProxy({
            listing_id: listingId,
            amount: parseNumber(values.proxyAmount),
          });
        } else if (!!openProxy) {
          await deleteProxy(openProxy.id);
        }
        handleRefreshProxy();
      }
      if (!isEqual(values.offerAmount, initialEditOfferValues.offerAmount)) {
        setErrorMessage("");
        await increaseOffer(
          listingId,
          {
            valuation_id: valuationId,
            amount: parseNumber(values.offerAmount),
          },
          posthog,
        );
      }
      setStep(s => s + 1);
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : DEFAULT_ERROR_MESSAGE);
      setIsLoading(false);
    }
  };

  const handleRefreshListingWithLoadingState = async () => {
    try {
      setIsLoading(true);
      await handleRefreshListing();
    } catch (e) {
      // pass
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Modal
      title={titleByStep[step]}
      open
      onClose={handleClose}
      onBack={makeOnBackHandler()}
    >
      {step === 0 && (
        <EditOfferForm
          onSave={async v => {
            setValues(v);
            await handleRefreshListingWithLoadingState();
          }}
          onSaveSuccess={() => setStep(s => s + 1)}
          initialValues={values}
          buyNowPrice={buyNowPrice}
          highestOpenBuyerOfferAmount={highestOpenBuyerOfferAmount}
          ltm={ltm}
          listingProxyIncrementAmount={listingProxyIncrementAmount}
          marketplaceMedian={marketplaceMedian}
          marketplaceMedianMultiplier={marketplaceMedianMultiplier}
          marketplaceMedianSource={marketplaceMedianSource}
          offer={offer}
          openProxyAmount={openProxy?.amount}
        />
      )}
      {step === 1 && (
        <>
          <Typography component="div" gutterBottom>
            <Box fontSize={13} textAlign="center">
              The seller will be immediately notified of your offer.
            </Box>
          </Typography>
          <Box m={2} textAlign="center">
            <Box fontSize={18} m={1}>
              Your Offer Amount
            </Box>
            <Box
              fontSize={18}
              fontWeight={theme.typography.fontWeightMedium}
              m={1}
            >
              {formatCurrencyAmount("USD", parseNumber(values.offerAmount))}
              {!!values.proxyAmount && (
                <>
                  <br />
                  <Box component="span" color={theme.palette.text.secondary}>
                    {` (Proxy: ${formatCurrencyAmount(
                      "USD",
                      parseNumber(values.proxyAmount),
                    )})`}
                  </Box>
                </>
              )}
            </Box>
          </Box>
          <Divider />
          <Box m={2} textAlign="center">
            <Typography component="div">
              <Box fontSize={13} textAlign="center" m={2}>
                Confirmed Earning Statistics
              </Box>
              {statisticsChanged && (
                <WarningMessage
                  title="Statistics Changed"
                  description={
                    "The earning statistics have changed for " +
                    "this catalog. Please check to confirm before " +
                    "proceeding."
                  }
                />
              )}
              {errorMessage && (
                <Box my={2}>
                  <ErrorMessage
                    title="Request Failed"
                    description={errorMessage}
                  />
                </Box>
              )}
              <Box m={2}>
                <Box fontSize={15}>Earns Through</Box>
                <Box
                  fontSize={15}
                  fontWeight={theme.typography.fontWeightMedium}
                >
                  {formatDate(termExpiration)}
                </Box>
              </Box>
              <Box m={2}>
                <Box fontSize={15}>List Price</Box>
                <Box
                  fontSize={15}
                  fontWeight={theme.typography.fontWeightMedium}
                >
                  {formatCurrencyAmount("USD", buyNowPrice || null)}
                </Box>
              </Box>
              <Box m={2}>
                <Box fontSize={15}>Last 12 Months&apos; Earnings</Box>
                <Box
                  fontSize={15}
                  fontWeight={theme.typography.fontWeightMedium}
                >
                  {formatAmount(ltm)}
                </Box>
              </Box>
              <Box m={2}>
                <Box fontSize={15}>Dollar Age</Box>
                <Box
                  fontSize={15}
                  fontWeight={theme.typography.fontWeightMedium}
                >
                  {formatNumber(dollarAge, " Years")}
                </Box>
              </Box>
            </Typography>
          </Box>
          <Box m={2} textAlign="center">
            <TransactionFeeNotice
              isPurchasing={false}
              price={parseNumber(values.offerAmount)}
            />
            <TermsAndConditions />
            <Box m={2}>
              <Button
                customColor="green"
                size="large"
                variant="contained"
                fullWidth
                onClick={handleIncreaseYourOffer}
                disabled={isLoading}
                isLoading={isLoading}
                id="confirm-offer"
              >
                Confirm Offer
              </Button>
            </Box>
          </Box>
        </>
      )}
      {step === 2 && (
        <>
          <Typography component="div">
            <Box textAlign="center">
              <CheckCircleIcon
                style={{
                  fontSize: circleIconSize.big,
                  color: theme.custom.palette.green.main,
                }}
              />
            </Box>
            <Box fontSize={15} textAlign="center" m={2}>
              Your offer has been successfully edited.
            </Box>
          </Typography>
        </>
      )}
    </Modal>
  );
};

export default EditOfferModal;
